import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

const PRESET_FONT_STACKS = {
  display: 'Outfit, system-ui, sans-serif',
  sans: '"IBM Plex Sans", system-ui, sans-serif',
  serif: '"Source Serif Pro", Georgia, serif',
  mono: '"IBM Plex Mono", ui-monospace, monospace',
};

/**
 * Resolve `font_family` (admin-typed value) to an actual CSS
 * font-family stack. Built-in slots map to already-loaded site fonts;
 * anything else is treated as a Google Font family name and is loaded
 * lazily by `ensureGoogleFont` below.
 */
function fontStack(family) {
  const key = (family || "").trim();
  if (!key) return PRESET_FONT_STACKS.display;
  if (PRESET_FONT_STACKS[key]) return PRESET_FONT_STACKS[key];
  return `"${key}", system-ui, sans-serif`;
}

const GFONT_LOADED = new Set();

/** Inject a <link rel=stylesheet> for the given Google Font family if
 *  we haven't already done so this page-view. No-op for built-in
 *  preset slots and obvious garbage input. */
function ensureGoogleFont(family) {
  const key = (family || "").trim();
  if (!key) return;
  if (PRESET_FONT_STACKS[key]) return;
  if (GFONT_LOADED.has(key)) return;
  // Strip any quotes the admin may have typed.
  const clean = key.replace(/['"]/g, "");
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(clean).replace(/%20/g, "+")}:wght@400;700&display=swap`;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.countdownFont = clean;
  document.head.appendChild(link);
  GFONT_LOADED.add(key);
}

function diffBuckets(ms, showSeconds) {
  if (ms <= 0) {
    return showSeconds
      ? [
          { label: "Days", value: 0 },
          { label: "Hours", value: 0 },
          { label: "Min", value: 0 },
          { label: "Sec", value: 0 },
        ]
      : [
          { label: "Days", value: 0 },
          { label: "Hours", value: 0 },
          { label: "Min", value: 0 },
        ];
  }
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const out = [
    { label: "Days", value: days },
    { label: "Hours", value: hours },
    { label: "Min", value: minutes },
  ];
  if (showSeconds) out.push({ label: "Sec", value: seconds });
  return out;
}

/**
 * Live countdown card. Two modes:
 *
 *   • manual         — counts down to `target_iso`
 *   • next_fixture   — pulls Coventry's next scheduled fixture from
 *                      /api/matches and counts down to kick-off; also
 *                      shows the opponent + competition under the
 *                      timer.
 *
 * Renders nothing when the widget is disabled or no target is known.
 */
export default function CountdownCard({ config }) {
  const enabled = !!(config && config.enabled);
  const mode = (config && config.mode) || "manual";
  const title = (config && config.title) || "";
  const manualTarget = (config && config.target_iso) || "";
  const bgUrl = (config && config.background_image_url) || "";
  const textColor = (config && config.text_color) || "#FFFFFF";
  const fontFamily = (config && config.font_family) || "display";
  const showSeconds = config && config.show_seconds !== false;
  const stroke = !!(config && config.stroke);
  // 0–100. Default 100 (no change). Lower = fade the image so the
  // navy fallback colour shows through. Saved as part of the
  // countdown widget config.
  const bgOpacity = Math.max(
    0,
    Math.min(100, Number(config?.background_opacity ?? 100)),
  );

  const [autoFixture, setAutoFixture] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const intervalRef = useRef(null);

  // Lazy-load Google Font if needed.
  useEffect(() => {
    if (enabled) ensureGoogleFont(fontFamily);
  }, [enabled, fontFamily]);

  // Tick once a second.
  useEffect(() => {
    if (!enabled) return undefined;
    intervalRef.current = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalRef.current);
  }, [enabled]);

  // If we're in next_fixture mode, fetch + refresh every 10 min.
  useEffect(() => {
    if (!enabled || mode !== "next_fixture") {
      setAutoFixture(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/matches", {
          params: { upcoming_limit: 1, results_limit: 0 },
        });
        if (cancelled) return;
        const next = (data.fixtures || [])[0] || null;
        setAutoFixture(next);
      } catch (_e) {
        if (!cancelled) setAutoFixture(null);
      }
    };
    load();
    const t = window.setInterval(load, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [enabled, mode]);

  const targetMs = useMemo(() => {
    if (mode === "next_fixture") {
      return autoFixture && autoFixture.utc_date
        ? Date.parse(autoFixture.utc_date)
        : null;
    }
    if (!manualTarget) return null;
    const t = Date.parse(manualTarget);
    return Number.isFinite(t) ? t : null;
  }, [mode, manualTarget, autoFixture]);

  if (!enabled) return null;
  if (mode === "manual" && !targetMs) return null;
  // next_fixture mode renders even without a target — we want the
  // "next fixture coming soon" placeholder so the layout doesn't jump.

  const buckets = targetMs ? diffBuckets(targetMs - now, showSeconds) : null;

  // Subtitle for next_fixture mode — show opponent + comp.
  let fixtureSubtitle = "";
  if (mode === "next_fixture" && autoFixture) {
    const opponent = autoFixture.coventry_is_home
      ? autoFixture.away && autoFixture.away.short_name
      : autoFixture.home && autoFixture.home.short_name;
    const venue = autoFixture.coventry_is_home ? "vs" : "@";
    fixtureSubtitle = [venue, opponent, autoFixture.competition]
      .filter(Boolean)
      .join(" · ");
  }

  const wrapperStyle = {
    color: textColor,
    fontFamily: fontStack(fontFamily),
    // Solid navy stays the base so a partially-faded image still has
    // a deliberate background colour to blend into.
    backgroundColor: "#0A1A33",
    // White outer stroke (toggleable via the `stroke` config flag).
    // 1px to match the surrounding image cards and hero. box-shadow
    // keeps the corner radius and renders cleanly over a background
    // image (a `border:` rule shifts layout when toggled).
    boxShadow: stroke ? "0 0 0 1px #FFFFFF" : undefined,
  };

  return (
    <div
      className="relative overflow-hidden rounded-md border border-black/10 bg-cover bg-center bg-no-repeat p-5 sm:p-6"
      style={wrapperStyle}
      data-testid="home-countdown"
    >
      {/* Background image as its own layer so we can fade it
          independently of the dark legibility overlay above. */}
      {bgUrl ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bgUrl})`, opacity: bgOpacity / 100 }}
        />
      ) : null}

      {/* Dark overlay so light text stays legible — scales with the
          image opacity so a faded image doesn't get double-dimmed. */}
      {bgUrl ? (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{ background: `rgba(0,0,0,${0.35 * (bgOpacity / 100)})` }}
        />
      ) : null}

      <div className="relative z-10">
        {title ? (
          <p
            className="mb-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90"
            data-testid="home-countdown-title"
          >
            {title}
          </p>
        ) : null}

        {fixtureSubtitle ? (
          <p
            className="mb-3 text-sm font-semibold opacity-95"
            data-testid="home-countdown-subtitle"
          >
            {fixtureSubtitle}
          </p>
        ) : null}

        {buckets ? (
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
            }}
            data-testid="home-countdown-buckets"
          >
            {buckets.map((b) => (
              <div key={b.label} className="text-center">
                <div
                  className="font-display text-3xl font-black leading-none sm:text-4xl"
                  style={{ fontFamily: fontStack(fontFamily) }}
                >
                  {String(b.value).padStart(2, "0")}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest opacity-80">
                  {b.label}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p
            className="text-sm italic opacity-80"
            data-testid="home-countdown-placeholder"
          >
            Next fixture announcement pending.
          </p>
        )}
      </div>
    </div>
  );
}
